import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import useLMSAuth from '../hooks/useLMSAuth';
import { ssoLogout } from '../../Common/Utils/SsoSession';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  background: ${({ theme }) => theme.colors.neutral[50] || '#f8fafc'};
  padding: 56px 16px;
  box-sizing: border-box;
`;

const Inner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

const ProfileCard = styled.div`
  background: linear-gradient(135deg, #00292e, #0a8294);
  border-radius: 16px;
  padding: 36px 40px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.4s ease both;
`;

const Avatar = styled.div`
  width: 72px; height: 72px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.6rem; font-weight: 900;
`;

const Profile = styled.div`
  display: flex; align-items: center; gap: 18px;
`;

const NameWrap = styled.div`
  .label { font-size: 0.78rem; opacity: 0.7; letter-spacing: 1px; text-transform: uppercase; }
  .name  { font-size: 1.45rem; font-weight: 900; margin-top: 4px; }
  .id    { font-size: 0.88rem; opacity: 0.85; margin-top: 2px; }
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  background: ${({ $role }) => $role === 'admin' ? '#fef3c7' : $role === 'instructor' ? '#dcfce7' : 'rgba(255,255,255,0.2)'};
  color: ${({ $role }) => $role === 'admin' ? '#b45309' : $role === 'instructor' ? '#166534' : '#fff'};
  margin-top: 8px;
`;

const ActionRow = styled.div`
  display: flex; gap: 8px;
`;
const SecondaryBtn = styled.button`
  padding: 10px 18px;
  background: rgba(255,255,255,0.18);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex; align-items: center;
  &:hover { background: rgba(255,255,255,0.28); }
`;
const LogoutBtn = styled(SecondaryBtn)`
  background: rgba(220, 38, 38, 0.7);
  border-color: rgba(220, 38, 38, 0.9);
  &:hover { background: rgba(220, 38, 38, 0.9); }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin-bottom: 36px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const QuickCard = styled(Link)`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 22px 22px 18px;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s;
  display: flex; flex-direction: column; gap: 8px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
    border-color: #67e8f9;
  }
`;
const QuickIcon = styled.div`
  width: 38px; height: 38px; border-radius: 10px;
  background: ${({ $color }) => $color};
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
  margin-bottom: 6px;
`;
const QuickTitle = styled.div`
  font-size: 1.02rem; font-weight: 800; color: #0f172a;
`;
const QuickDesc = styled.div`
  font-size: 0.85rem; color: #64748b; line-height: 1.55;
`;

const SectionTitle = styled.h3`
  font-size: 1.05rem; font-weight: 900; margin: 0 0 14px; color: #0f172a;
`;

const SectionRow = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  margin: 32px 0 14px;
`;
const SeeAll = styled(Link)`
  font-size: 0.85rem; font-weight: 700;
  color: ${({ theme }) => theme.colors.primary[700]};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

/* ── 내 강의 카드 ── */
const ClassGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;
const LearningPanel = styled.section`
  position: relative;
  overflow: hidden;
  padding: 26px;
  border: 1px solid #bae6fd;
  border-radius: 18px;
  background: radial-gradient(circle at 92% 8%, rgba(34,211,238,.2), transparent 30%), linear-gradient(135deg, #f0fdfa 0%, #eff6ff 58%, #f5f3ff 100%);
  box-shadow: 0 16px 40px rgba(14,116,144,.08);
`;
const LearningSummary = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
  margin-bottom: ${({ $hasClasses }) => $hasClasses ? '22px' : '0'};
  @media (max-width: 700px) { align-items: flex-start; flex-direction: column; }
`;
const LearningCopy = styled.div`
  max-width: 620px;
  .eyebrow { color: #0e7490; font-size: .76rem; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
  .headline { margin-top: 7px; color: #0f172a; font-size: 1.35rem; font-weight: 900; line-height: 1.35; }
  .description { margin-top: 7px; color: #475569; font-size: .9rem; line-height: 1.65; }
`;
const LearningCount = styled.div`
  min-width: 112px; padding: 16px 20px; border: 1px solid rgba(255,255,255,.9); border-radius: 14px;
  background: rgba(255,255,255,.72); text-align: center; box-shadow: 0 10px 24px rgba(14,116,144,.08);
  .number { color: #0e7490; font-size: 2rem; font-weight: 950; line-height: 1; }
  .label { margin-top: 6px; color: #64748b; font-size: .76rem; font-weight: 800; }
`;
const LearningActions = styled.div`
  display: flex; flex-wrap: wrap; gap: 9px; margin-top: 16px;
`;
const LearningAction = styled(Link)`
  display: inline-flex; align-items: center; justify-content: center; min-height: 40px; padding: 0 16px;
  border: 1px solid ${({ $primary }) => $primary ? '#0e7490' : '#cbd5e1'}; border-radius: 10px;
  background: ${({ $primary }) => $primary ? '#0e7490' : 'rgba(255,255,255,.84)'};
  color: ${({ $primary }) => $primary ? '#fff' : '#334155'}; font-size: .84rem; font-weight: 850; text-decoration: none;
  transition: transform .18s ease, box-shadow .18s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(14,116,144,.14); }
`;
const ClassCard = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  display: flex; flex-direction: column;
  transition: all 0.2s;
  &:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(15,23,42,0.08); border-color: #67e8f9; }
`;
const ClassThumb = styled.div`
  width: 100%; height: 130px;
  background: ${({ $url, $color }) => $url ? `url(${$url}) center/cover` : `linear-gradient(135deg, ${$color || '#0891b2'}, #0e7490)`};
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 1.6rem; font-weight: 900;
`;
const ClassBody = styled.div`padding: 14px 16px 16px;`;
const ClassTitle = styled.div`
  font-size: 0.98rem; font-weight: 800; color: #0f172a;
  margin-bottom: 4px; line-height: 1.4;
`;
const ClassMeta = styled.div`font-size: 0.78rem; color: #64748b;`;
const PlayBtn = styled.button`
  margin-top: 10px;
  width: 100%; padding: 8px;
  background: ${({ theme }) => theme.colors.primary[700]};
  color: #fff; -webkit-text-fill-color: #fff;
  border: none; border-radius: 8px;
  font-size: 0.83rem; font-weight: 800; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.primary[800] || '#155e75'}; }
`;
const EmptyClass = styled.div`
  background: #fff; border: 1px dashed #cbd5e1; border-radius: 12px;
  padding: 36px; text-align: center; color: #64748b;
  font-size: 0.9rem;
  a { color: #0891b2; font-weight: 700; text-decoration: none; margin-left: 6px; }
`;
const VideoOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.85);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
`;
const VideoBox = styled.div`
  background: #000; border-radius: 12px; width: 92%; max-width: 960px; position: relative;
`;
const VideoClose = styled.button`
  position: absolute; top: -36px; right: 0;
  background: none; border: none; color: #fff; font-size: 1.1rem; cursor: pointer;
`;

/* ── 일정 리스트 ── */
const ScheduleList = styled.div`
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  overflow: hidden;
`;
const ScheduleItem = styled.div`
  display: grid; grid-template-columns: 90px 1fr auto; gap: 16px;
  padding: 16px 20px; align-items: center;
  border-top: 1px solid #f1f5f9;
  &:first-child { border-top: none; }
`;
const SchedDate = styled.div`
  text-align: center;
  .month { font-size: 0.7rem; font-weight: 800; color: #64748b; letter-spacing: 1px; }
  .day { font-size: 1.6rem; font-weight: 900; color: #0f172a; line-height: 1; margin-top: 2px; }
  .dow { font-size: 0.72rem; font-weight: 700; margin-top: 4px;
    color: ${({ $sun, $sat }) => $sun ? '#dc2626' : $sat ? '#2563eb' : '#64748b'};
  }
`;
const SchedBody = styled.div`
  .title { font-size: 0.96rem; font-weight: 800; color: #0f172a; margin-bottom: 3px; }
  .meta  { font-size: 0.8rem; color: #64748b; }
  .course { color: #0891b2; font-weight: 700; }
`;
const SchedTime = styled.div`
  font-size: 0.85rem; font-weight: 800; color: #0e7490;
  background: #ecfeff; padding: 6px 10px; border-radius: 6px;
`;

/* ── 작업실 워크스페이스 미니 카드 ── */
const WSGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const WSCard = styled(Link)`
  display: flex; gap: 14px; align-items: center;
  padding: 18px 20px; background: #fff;
  border: 1px solid #e2e8f0; border-radius: 12px;
  text-decoration: none; color: inherit;
  transition: all 0.2s;
  border-left: 4px solid ${({ $color }) => $color};
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(15,23,42,0.06); border-color: ${({ $color }) => $color}; border-left-color: ${({ $color }) => $color}; }
`;
const WSIcon = styled.div`
  width: 42px; height: 42px; border-radius: 10px;
  background: ${({ $color }) => `${$color}1A`};
  color: ${({ $color }) => $color};
  display: flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 0.95rem; flex-shrink: 0;
`;
const WSText = styled.div`
  .title { font-size: 0.95rem; font-weight: 800; color: #0f172a; }
  .desc  { font-size: 0.78rem; color: #64748b; margin-top: 2px; line-height: 1.4; }
`;

const QUICK_LINKS_BASE = [
  { to: '/Enroll',      label: '수강신청',     desc: '신청 내역과 신규 신청', icon: '+', color: '#0891b2' },
  { to: '/Courses',     label: '수업과정',     desc: '전체 과정 둘러보기',   icon: '@', color: '#7c3aed' },
  { to: '/Instructors', label: '강사 소개',    desc: '담당 강사 프로필',     icon: '*', color: '#b45309' },
  { to: '/NewsBoard/0', label: '커뮤니티',     desc: '공지·자유·Q&A·후기',   icon: '!', color: '#4338ca' },
  { to: '/Help',        label: '고객센터',     desc: '문의·공지 확인',      icon: '?', color: '#dc2626' },
];

const WORKSPACES_LIST = [
  { color: '#2563eb', icon: 'WK', title: '작업관리',   desc: '프로젝트 태스크 관리', to: '/Developer' },
  { color: '#059669', icon: 'PF', title: '포트폴리오', desc: '작업 결과물 정리',     to: '/Portfolio' },
  { color: '#d97706', icon: 'CA', title: '일정관리',   desc: '프로젝트 일정/마일스톤', to: '/WorkCalendar' },
];

const KOREAN_DOW = ['일', '월', '화', '수', '목', '금', '토'];

const ADMIN_LINKS = [
  { to: '/Enroll?tab=manage', label: '수강신청 관리', desc: '대기 중인 신청 승인/반려', icon: '⚙', color: '#1e293b' },
];

export default function MyPagePage() {
  const history = useHistory();
  const { login, loading, name, user_id, role, refresh } = useLMSAuth();
  const [classes, setClasses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [video, setVideo] = useState(null);

  useEffect(() => {
    if (!loading && !login) history.replace('/Login?next=/MyPage');
  }, [loading, login, history]);

  // 내 강의 (승인된 과정) 로드
  useEffect(() => {
    if (!login) return;
    fetch('/lms/my-classroom', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setClasses(Array.isArray(data) ? data : []))
      .catch(() => setClasses([]));
  }, [login]);

  // 다가오는 30일 강의 일정
  useEffect(() => {
    const from = new Date().toISOString();
    const to = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    fetch(`/lms/schedules?from=${from}&to=${to}`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setSchedules(Array.isArray(data) ? data.slice(0, 5) : []))
      .catch(() => setSchedules([]));
  }, []);

  const playCourse = async (course) => {
    try {
      const r = await fetch(`/lms/courses/${course.id}/video`, { credentials: 'include' });
      if (r.status === 403) { alert('수강 승인이 필요합니다.'); return; }
      if (!r.ok) { alert('영상을 불러올 수 없습니다.'); return; }
      const d = await r.json();
      setVideo({ url: d.video_url, title: course.title });
    } catch {
      alert('영상을 불러올 수 없습니다.');
    }
  };

  const onLogout = async () => {
    if (!window.confirm('로그아웃 하시겠습니까?')) return;
    await ssoLogout();
    try {
      await fetch('/request/Logout', { credentials: 'include' });
    } catch { /* ignore */ }
    window.location.href = '/';
  };

  if (loading || !login) {
    return (
      <PageWrapper>
        <Inner style={{ textAlign: 'center', color: '#64748b', paddingTop: 80 }}>
          로그인 확인 중...
        </Inner>
      </PageWrapper>
    );
  }

  const initial = (name || user_id || '?').slice(0, 1).toUpperCase();

  return (
    <PageWrapper>
      <Inner>
        <ProfileCard>
          <Profile>
            <Avatar>{initial}</Avatar>
            <NameWrap>
              <div className="label">My Page</div>
              <div className="name">{name || user_id}</div>
              <div className="id">@{user_id}</div>
              <RoleBadge $role={role}>
                {role === 'admin' ? '관리자' : role === 'instructor' ? '강사' : '수강생'}
              </RoleBadge>
            </NameWrap>
          </Profile>
          <ActionRow>
            <SecondaryBtn as={Link} to="/MyInfoUpdate">회원정보 수정</SecondaryBtn>
            <LogoutBtn onClick={onLogout}>로그아웃</LogoutBtn>
          </ActionRow>
        </ProfileCard>

        {/* ── 내 수강 ── */}
        <SectionRow>
          <SectionTitle style={{ margin: 0 }}>내 수강</SectionTitle>
          {classes.length > 0 && <SeeAll to="/MyClassroom">전체 보기 →</SeeAll>}
        </SectionRow>
        <LearningPanel>
          <LearningSummary $hasClasses={classes.length > 0}>
            <LearningCopy>
              <div className="eyebrow">My Learning Space</div>
              <div className="headline">
                {classes.length > 0 ? `${name || '수강생'}님의 승인된 수업을 이어서 학습하세요.` : '첫 번째 시뮬레이션 수업을 시작해 보세요.'}
              </div>
              <div className="description">
                {classes.length > 0
                  ? '과정별 강의와 XR 시뮬레이션은 내 강의실에서 한 번에 확인할 수 있습니다.'
                  : '관심 과정을 신청하고 승인되면 강의, 실습 시뮬레이션, 일정이 이곳에 표시됩니다.'}
              </div>
              <LearningActions>
                <LearningAction $primary to={classes.length > 0 ? '/MyClassroom' : '/Enroll'}>
                  {classes.length > 0 ? '내 강의실 입장' : '수강 과정 찾기'}
                </LearningAction>
                <LearningAction to={classes.length > 0 ? '/Schedule' : '/Courses'}>
                  {classes.length > 0 ? '학습 일정 보기' : '전체 과정 둘러보기'}
                </LearningAction>
              </LearningActions>
            </LearningCopy>
            <LearningCount>
              <div className="number">{classes.length}</div>
              <div className="label">승인된 수강 과정</div>
            </LearningCount>
          </LearningSummary>
          {classes.length > 0 && (
            <ClassGrid>
              {classes.slice(0, 6).map((c) => (
                <ClassCard key={c.id}>
                  <ClassThumb $url={c.thumbnail_url}>
                    {!c.thumbnail_url && 'XR'}
                  </ClassThumb>
                  <ClassBody>
                    <ClassTitle>{c.title}</ClassTitle>
                    <ClassMeta>강사: {c.instructor_name || '미정'}</ClassMeta>
                    <PlayBtn onClick={() => playCourse(c)}>강의 시청</PlayBtn>
                  </ClassBody>
                </ClassCard>
              ))}
            </ClassGrid>
          )}
        </LearningPanel>

        {/* ── 다가오는 강의 일정 ── */}
        <SectionRow>
          <SectionTitle style={{ margin: 0 }}>다가오는 강의 일정</SectionTitle>
          <SeeAll to="/Schedule">전체 캘린더 →</SeeAll>
        </SectionRow>
        {schedules.length === 0 ? (
          <EmptyClass>예정된 일정이 없습니다.</EmptyClass>
        ) : (
          <ScheduleList>
            {schedules.map((s) => {
              const d = new Date(s.start_at);
              const dow = d.getDay();
              return (
                <ScheduleItem key={s.id}>
                  <SchedDate $sun={dow === 0} $sat={dow === 6}>
                    <div className="month">{d.getMonth() + 1}월</div>
                    <div className="day">{d.getDate()}</div>
                    <div className="dow">{KOREAN_DOW[dow]}</div>
                  </SchedDate>
                  <SchedBody>
                    <div className="title">{s.title}</div>
                    <div className="meta">
                      {s.location && `${s.location} · `}
                      {s.course_title && <span className="course">📚 {s.course_title}</span>}
                    </div>
                  </SchedBody>
                  <SchedTime>
                    {String(d.getHours()).padStart(2, '0')}:{String(d.getMinutes()).padStart(2, '0')}
                  </SchedTime>
                </ScheduleItem>
              );
            })}
          </ScheduleList>
        )}

        {/* ── 내 작업실 ── */}
        <SectionRow>
          <SectionTitle style={{ margin: 0 }}>내 작업실</SectionTitle>
          <SeeAll to="/WorkRoom">전체 작업실 →</SeeAll>
        </SectionRow>
        <WSGrid>
          {WORKSPACES_LIST.map((w) => (
            <WSCard key={w.title} to={w.to} $color={w.color}>
              <WSIcon $color={w.color}>{w.icon}</WSIcon>
              <WSText>
                <div className="title">{w.title}</div>
                <div className="desc">{w.desc}</div>
              </WSText>
            </WSCard>
          ))}
        </WSGrid>

        <SectionRow>
          <SectionTitle style={{ margin: 0 }}>빠른 메뉴</SectionTitle>
        </SectionRow>
        <Grid>
          {QUICK_LINKS_BASE.map((q) => (
            <QuickCard key={q.to} to={q.to}>
              <QuickIcon $color={q.color}>{q.icon}</QuickIcon>
              <QuickTitle>{q.label}</QuickTitle>
              <QuickDesc>{q.desc}</QuickDesc>
            </QuickCard>
          ))}
        </Grid>

        {(role === 'admin' || role === 'instructor') && (
          <>
            <SectionRow>
              <SectionTitle style={{ margin: 0 }}>관리자 도구</SectionTitle>
            </SectionRow>
            <Grid>
              {ADMIN_LINKS.map((q) => (
                <QuickCard key={q.to} to={q.to}>
                  <QuickIcon $color={q.color}>{q.icon}</QuickIcon>
                  <QuickTitle>{q.label}</QuickTitle>
                  <QuickDesc>{q.desc}</QuickDesc>
                </QuickCard>
              ))}
            </Grid>
          </>
        )}
      </Inner>

      {video && (
        <VideoOverlay onClick={() => setVideo(null)}>
          <VideoBox onClick={(e) => e.stopPropagation()}>
            <VideoClose onClick={() => setVideo(null)}>✕ 닫기</VideoClose>
            {video.url ? (
              video.url.includes('youtu') ? (
                <iframe
                  src={video.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  width="100%" height="540" allow="autoplay; encrypted-media" allowFullScreen
                  style={{ border: 0, borderRadius: 12 }} title={video.title}
                />
              ) : (
                <video src={video.url} controls autoPlay style={{ width: '100%', borderRadius: 12 }} />
              )
            ) : (
              <div style={{ padding: 60, color: '#d1d5db', textAlign: 'center' }}>
                "{video.title}" 강의 영상이 아직 등록되지 않았습니다.
              </div>
            )}
          </VideoBox>
        </VideoOverlay>
      )}
    </PageWrapper>
  );
}
